/**
 * Dashboard page (BUILD_SPEC.md §10.2).
 *
 * Polls `/api/transactions?status=ACTIVE&include=actions` every 30 s
 * via React Query so the office TV stays current without manual
 * refreshes. Renders three flavors of UI based on the query state:
 *   • loading on first load — minimal skeleton, no full-page spinner
 *     (the empty state below would flash for hundreds of ms otherwise);
 *   • error — friendly card with retry, used for the "API down" path
 *     that criterion 14 exercises;
 *   • success with empty list — the §21 step 30 empty-state copy;
 *   • success with items — TransactionGrid.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Inbox, AlertOctagon } from "lucide-react";
import { listTransactionsWithActions } from "@/api/transactions";
import { TransactionGrid } from "@/components/Grid";
import { SoundUnlockBanner } from "@/components/SoundUnlockBanner";
import {
  DashboardFilters,
  readPersistedFilters,
  type FilterState,
} from "@/components/DashboardFilters";
import { useDashboardSounds } from "@/hooks/useDashboardSounds";
import { minutesSince } from "@/lib/dates";
import { normalizeArabic } from "../../../../packages/keywords/index.js";
import type { Action, Transaction } from "@/api/types";

const POLL_INTERVAL_MS = 30_000;

/** v1.1 B7 — predicate that decides whether a single dashboard item
 *  passes the active filter. Pure function; takes the item + filter
 *  state and returns boolean.
 *
 *  Search uses normalizeArabic on BOTH sides so spelling variants
 *  match — distinct from the v1.1 B1 flash detector which uses
 *  literal matching. The two are intentionally different: flash is a
 *  loud TV signal that benefits from low false-positives; search is
 *  staff typing into a box where forgiveness is the kinder default. */
function matchesFilter(
  item: { transaction: Transaction; actions: Action[] },
  state: FilterState,
): boolean {
  const { transaction: tx, actions } = item;

  // Search: substring match across all the user-visible text fields
  // after normalization. Empty search short-circuits true.
  if (state.search.trim().length > 0) {
    const needle = normalizeArabic(state.search);
    const haystackParts: string[] = [
      tx.ownerName ?? "",
      tx.requestNumber,
      tx.region,
      tx.serviceType,
    ];
    for (const a of actions) {
      haystackParts.push(a.department, a.status, a.finalAction ?? "");
    }
    const hay = normalizeArabic(haystackParts.join(" "));
    if (!hay.includes(needle)) return false;
  }

  // Status pill — exact match on actions[0].status (the latest action's
  // status field). Aligned with v1.1 B2's color-pill rules.
  if (state.status !== "any") {
    if (actions[0]?.status !== state.status) return false;
  }

  // Region + service-type multi-select. Empty array means "no
  // restriction"; a populated array narrows to the union.
  if (state.regions.length > 0 && !state.regions.includes(tx.region)) return false;
  if (state.serviceTypes.length > 0 && !state.serviceTypes.includes(tx.serviceType)) return false;

  // Time bucket — "changed within N hours". `lastChangedAt` is the
  // signal; null lastChangedAt means "never changed since add" and
  // automatically fails any time filter.
  if (state.timeBucket !== "any") {
    if (!tx.lastChangedAt) return false;
    const hours = minutesSince(tx.lastChangedAt) / 60;
    const limit =
      state.timeBucket === "1h"
        ? 1
        : state.timeBucket === "6h"
          ? 6
          : state.timeBucket === "24h"
            ? 24
            : 72;
    if (hours > limit) return false;
  }

  return true;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterState>(() => readPersistedFilters());
  const query = useQuery({
    queryKey: ["transactions", "dashboard"],
    queryFn: () => listTransactionsWithActions({ status: "ACTIVE", limit: 200 }),
    refetchInterval: POLL_INTERVAL_MS,
    // The query becomes "stale" instantly so a tab refocus triggers
    // an immediate refetch — the 30s interval is the floor, not a
    // throttle on user interactions.
    staleTime: 0,
  });

  // Memoize the items array reference so the sound hook only re-runs
  // when the underlying data changes, not on every render.
  const items = useMemo(
    () => query.data?.items.map((t) => ({ transaction: t, actions: t.actions ?? [] })),
    [query.data],
  );
  useDashboardSounds({ items, ready: query.isSuccess });

  // v1.1 hotfix: this memo MUST run on every render, before any
  // conditional return, otherwise React sees a different hook count
  // between the loading (no items) and success (items present)
  // renders and crashes with error 310 ("Rendered more hooks than
  // during the previous render"). The original placement after the
  // `query.isPending` / `query.isError` early returns produced exactly
  // that order mismatch in the production build, where minified React
  // doesn't fall back to the dev-mode warning. `items ?? []` makes
  // the memo safe to run during loading.
  const list = useMemo(() => items ?? [], [items]);
  const filteredList = useMemo(
    () => list.filter((item) => matchesFilter(item, filters)),
    [list, filters],
  );

  if (query.isPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <p className="text-sm text-muted dark:text-muted-dark">{t("common.loading")}</p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-rejection-bg dark:bg-rejection-dark-bg/40">
            <AlertOctagon className="h-8 w-8 text-rejection dark:text-rejection-dark" aria-hidden />
          </div>
          <h2 className="text-xl font-bold text-fg dark:text-fg-dark mb-2">
            {t("common.errorTitle")}
          </h2>
          <p className="text-sm text-muted dark:text-muted-dark mb-4">{t("common.errorHint")}</p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="rounded-md bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark px-4 py-2 text-sm font-semibold hover:opacity-90"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <>
        <SoundUnlockBanner />
        <div className="flex min-h-[60vh] items-center justify-center px-4 py-16">
          <div className="text-center max-w-md">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-hairline/40 dark:bg-hairline-dark/40">
              <Inbox className="h-8 w-8 text-muted dark:text-muted-dark" aria-hidden />
            </div>
            <h2 className="rtl-isolate text-xl font-bold text-fg dark:text-fg-dark mb-2">
              {t("dashboard.emptyTitle")}
            </h2>
            <p className="text-sm text-muted dark:text-muted-dark">{t("dashboard.emptyHint")}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <SoundUnlockBanner />
      <div className="px-4 pt-3 shrink-0">
        <DashboardFilters
          state={filters}
          onChange={setFilters}
          resultCount={filteredList.length}
        />
      </div>
      <div className="flex-1 min-h-0">
        <TransactionGrid items={filteredList} />
      </div>
    </div>
  );
}
